import {
  Avatar,
  Button,
  Dialog,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  StepIconProps,
  SvgIcon,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import CheckIcon from '@untitled-ui/icons-react/build/esm/Check';
import Loading01 from '@untitled-ui/icons-react/build/esm/Loading01';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Team } from 'src/API';
import { teamApi } from 'src/api/team';
import { useAuth } from 'src/hooks/use-auth';
import { useHasDefaultOrganization } from 'src/hooks/use-has-default-organization';
import { useRouter } from 'src/hooks/use-router';
import { paths } from 'src/paths';
import { useDispatch } from 'src/store';
import { thunks as teamThunk } from 'src/thunks/team';
import { CreationState } from './onboarding-create-form';

type OnboardingCreationStepProps = {
  data: CreationState;
  onTeamCreated?: (teamId: string) => void;
};

const StepIcon: FC<StepIconProps> = (props) => {
  let { active, completed, icon } = props;

  return (
    <Avatar
      sx={{
        height: 40,
        width: 40,
        ...(active && {
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
        }),
        ...(completed && {
          backgroundColor: 'success.main',
          color: 'primary.contrastText',
        }),
      }}
      variant="rounded"
    >
      {completed ? (
        <SvgIcon>
          <CheckIcon />
        </SvgIcon>
      ) : active ? (
        <SvgIcon
          style={{
            animation: 'spin 2s infinite linear',
          }}
        >
          <Loading01 />
        </SvgIcon>
      ) : (
        icon
      )}
    </Avatar>
  );
};

type StepCreationProps = {
  promise: (data) => Promise<any>;
  completedIndex: number;
  data: any;
};

// Custom hook to handle the process
const useTeamCreation = (inputData: CreationState, onTeamCreated?: (teamId: string) => void) => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [completed, setCompleted] = useState<number[]>([]);
  const [team, setTeam] = useState<Team | undefined | null>(null);
  const dispatch = useDispatch();
  const { user, refreshUser } = useAuth();
  const hasDefaultOrganization = useHasDefaultOrganization();

  useEffect(() => {
    if (completed.length !== 0) return;
    const createTeamAndShare = async () => {
      // await new Promise(r => setTimeout(r, 1000));
      if (!inputData.teamInformation?.name) return;

      // If default organization exists and no explicit sharing is configured,
      // pass null to let backend automatically assign default organization
      const managerGroups =
        hasDefaultOrganization && (!inputData.share || inputData.share.length === 0) ? null : inputData.share;

      console.log('🏢 ONBOARDING: Team creation manager groups logic', {
        hasDefaultOrganization,
        inputDataShare: inputData.share,
        finalManagerGroups: managerGroups,
        teamName: inputData.teamInformation?.name,
      });

      const createResult = await teamApi.create({
        name: inputData.teamInformation?.name,
        description: inputData.teamInformation?.description,
        teamSize: inputData.teamInformation?.teamSize,
        valueProposition: inputData.teamInformation?.valueProposition,
        managerGroups,
      });

      // Refresh user session to get updated group claims (user added to admin group)
      console.log('🔄 ONBOARDING: Refreshing user session after team creation');
      try {
        await refreshUser();
        console.log('✅ ONBOARDING: User session refreshed successfully');
      } catch (error) {
        console.error('❌ ONBOARDING: Failed to refresh user session:', error);
        // Don't fail team creation if token refresh fails
      }

      return createResult;
    };

    async function runCreationSteps() {
      let createResult;
      try {
        createResult = await createTeamAndShare();
        setActiveStep(1);
        setCompleted([0]);
      } catch (error) {
        console.error('Failed to create team', error);
        setCompleted([]);
        toast.error('Failed to create team');
      }

      try {
        if (!createResult.ok) return;
        console.log('🎯 ONBOARDING: Team created successfully', {
          teamId: createResult.result.id,
          teamName: createResult.result.name,
          team: createResult.result,
        });

        setTeam(createResult.result);

        // Immediately update Redux state with the newly created team
        console.log('🏪 ONBOARDING: Updating Redux with newly created team', {
          teamId: createResult.result.id,
          teamName: createResult.result.name,
        });
        dispatch(teamThunk.setTeam({ team: createResult.result }));

        // Navigate immediately after Redux update to prevent rendering with wrong URL
        console.log('🧭 ONBOARDING: Navigating to new team overview to avoid URL/state mismatch');
        window.location.href = paths.team.overview(createResult.result.id);

        // Notify parent component about team creation
        if (onTeamCreated) {
          console.log('📞 ONBOARDING: Calling onTeamCreated callback with teamId:', createResult.result.id);
          onTeamCreated(createResult.result.id);
        }

        // IMPORTANT: Update Redux store with the newly created team
        // Use basic query to avoid moduleConfigurations permission issues
        console.log('🔄 ONBOARDING: Loading teams into Redux store with teamId (basic):', createResult.result.id);
        await dispatch(teamThunk.getBasicTeams({ teamId: createResult.result.id }));
        console.log('✅ ONBOARDING: Basic teams loaded into Redux store');

        // remove current user from the list
        inputData.members = inputData.members.filter((f) => f.email !== user?.email);
        const memberPromises = inputData.members.map((member) => {
          return teamApi.addUserToTeam(createResult.result.id, member.email, member.role);
        });
        await Promise.all(memberPromises);
        setCompleted((s) => [...s, 1]);
        console.log('🎉 ONBOARDING: Team creation flow completed');
      } catch (error) {
        console.error('❌ ONBOARDING: Failed to create team', error);
        toast.error('Failed to create team');
      }
    }
    runCreationSteps();
  }, [inputData, completed.length, dispatch, hasDefaultOrganization, onTeamCreated]); // Dependency array includes inputData to re-run the effect if it changes

  return { activeStep, completed, team };
};
export default function OnboardingCreationStep({ data, onTeamCreated }: OnboardingCreationStepProps) {
  const { activeStep, completed, team } = useTeamCreation(data, onTeamCreated);
  const router = useRouter();
  const dispatch = useDispatch();
  const { refreshUser } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(true);
  const [isNavigating, setIsNavigating] = useState(false);

  const steps = useMemo(() => {
    return [
      {
        label: 'Creating',
        text: 'We are creating a new team for you and sharing with your team mates.',
      },
      {
        label: 'Inviting',
        text: 'We are inviting team members to join your team.',
      },
    ];
  }, []);

  const handleGoToTeam = useCallback(async () => {
    if (isNavigating || !team?.id) return; // Prevent multiple clicks and ensure team exists

    console.log('🚀 GO TO TEAM: Button clicked', {
      teamId: team?.id,
      teamName: team?.name,
      isNavigating,
    });
    setIsNavigating(true);

    try {
      // Validate team ID before proceeding
      if (!team.id || team.id === 'undefined' || team.id === 'null') {
        throw new Error(`Invalid team ID: ${team.id}`);
      }

      // Ensure the created team is set as current in Redux
      console.log('🏪 GO TO TEAM: Setting team as current in Redux', {
        teamId: team.id,
        teamName: team.name,
        timestamp: new Date().toISOString(),
      });
      dispatch(teamThunk.setTeam({ team: team }));

      // Ensure teams are loaded in Redux store with correct current team
      // Try full query first, fallback to basic if permissions fail
      console.log('🔄 GO TO TEAM: Loading teams into Redux store with teamId:', team.id);
      try {
        await dispatch(teamThunk.getTeams({ teamId: team.id }));
        console.log('✅ GO TO TEAM: Full teams loaded into Redux store');
      } catch (fullQueryError) {
        console.log('⚠️ GO TO TEAM: Full query failed, trying basic teams:', fullQueryError?.message);
        await dispatch(teamThunk.getBasicTeams({ teamId: team.id }));
        console.log('✅ GO TO TEAM: Basic teams loaded into Redux store');
      }

      // Refresh user session for updated groups
      console.log('🔄 GO TO TEAM: Refreshing user session');
      await refreshUser();
      console.log('✅ GO TO TEAM: User session refreshed');

      // Close dialog after everything is ready
      console.log('🚪 GO TO TEAM: Closing dialog');
      setDialogOpen(false);

      // Navigate to team using the validated team ID
      const targetUrl = paths.team.overview(team.id);
      console.log('🧭 GO TO TEAM: Final navigation step', {
        teamId: team.id,
        targetUrl,
        timestamp: new Date().toISOString(),
      });

      // Extra validation before navigation
      if (!targetUrl || targetUrl.includes('undefined') || targetUrl.includes('null')) {
        throw new Error(`Invalid target URL generated: ${targetUrl} from team ID: ${team.id}`);
      }

      router.push(targetUrl);
      console.log('✅ GO TO TEAM: Navigation initiated successfully to:', targetUrl);
    } catch (error) {
      console.error('❌ GO TO TEAM: Error occurred', {
        error: error.message,
        teamId: team?.id,
        teamName: team?.name,
      });
      // Still navigate even if refresh fails, but with error handling
      setDialogOpen(false);
      if (team?.id && team.id !== 'undefined' && team.id !== 'null') {
        router.push(paths.team.overview(team.id));
      } else {
        console.error('❌ GO TO TEAM: Cannot navigate - invalid team ID');
      }
    } finally {
      setIsNavigating(false); // Always reset loading state
    }
  }, [dispatch, refreshUser, router, team, isNavigating]);

  return (
    <Dialog fullScreen={true} open={dialogOpen}>
      <style>
        {`
            @keyframes spin {
                 0% { transform: rotate(-360deg); }
                 100% { transform: rotate(0deg); }
            }`}
      </style>
      <Grid container spacing={3}>
        <Grid item xs={1}></Grid>
        <Grid item xs={8}>
          <Stack
            direction={'column'}
            spacing={2}
            alignContent={'center'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <List>
              {steps.map((step, index) => {
                return (
                  <ListItem key={step.label + index}>
                    <ListItemIcon>
                      <StepIcon
                        active={activeStep === index}
                        completed={completed.indexOf(index) > -1}
                        icon={<CheckIcon />}
                      ></StepIcon>
                    </ListItemIcon>
                    <ListItemText>
                      <Typography variant="h6">{step.label}</Typography>
                      <Typography variant="body1">{step.text}</Typography>
                    </ListItemText>
                  </ListItem>
                );
              })}
            </List>
            <div>
              {team && (
                <Button variant="contained" color="primary" disabled={isNavigating} onClick={handleGoToTeam}>
                  {isNavigating ? 'Loading...' : 'Go to team'}
                </Button>
              )}
            </div>
          </Stack>
        </Grid>
      </Grid>
    </Dialog>
  );
}
